<template>
  <div class="member-table">
    <base-table
      ref="baseTable"
      :data="filterData"
      frontend-pagination
      :show-pointer="false"
      :page-info="pageInfo"
    >
      <el-table-column
        v-for="column in tableColumns"
        :key="column.prop"
        :label="column.label"
        :prop="column.prop"
        :fixed="column.fixed"
        :min-width="column.width"
        show-overflow-tooltip
        align="center"
      >
        <template slot-scope="scope">
          <template v-if="column.prop === 'telephone'">
            {{ _.get(scope.row, column.prop) || _.get(scope.row, "email") }}
          </template>
          <template v-if="column.prop === 'status'">
            <el-tag v-if="scope.row.status === '已停用'" type="info" effect="dark" size="mini">
              已停用
            </el-tag>
            <el-tag
              v-else-if="scope.row.status === '未激活'"
              type="warning"
              effect="dark"
              size="mini"
            >
              未激活
            </el-tag>
            <el-tag v-else type="success" effect="dark" size="mini">正常</el-tag>
          </template>
          <template v-else>
            {{ _.get(scope.row, column.prop) }}
            <svg-icon
              v-if="column.prop === 'name' && scope.row.isMaster"
              class="admin-icon"
              icon-class="login-user"
            />
          </template>
        </template>
      </el-table-column>
      <el-table-column align="center" label="操作" width="120" fixed="right">
        <template slot-scope="scope">
          <el-button @click="$emit('edit-user', scope.row)" type="text" size="small">
            编辑
          </el-button>
          <el-button
            @click="$emit('switch-user-state', scope.row)"
            v-if="!currentCompany.syncDing"
            type="text"
            size="small"
          >
            {{ scope.row.companyScope.gone ? "启用" : "停用" }}
          </el-button>
        </template>
      </el-table-column>
    </base-table>
  </div>
</template>

<script>
import BaseTable from "@/components/UIComponents/BaseTable"
import { tableColumns } from "./static"
import { calcDepartmentIdAllUserIdSetMap } from "@/utils/user"
import { getCompanyRolesWithNum } from "@/api/userCenter/company"

export default {
  name: "member-table",
  components: { BaseTable },
  inject: ["curCompanyUsers", "rootDepartment", "currentCompany"],
  data() {
    return {
      tableColumns,
      pageInfo: {
        total: 0,
        currentPage: 1,
        pageSize: 20
      },
      currentDepartment: {},
      searchBody: {
        searchKey: "",
        searchText: ""
      },
      roles: [],
      // 缓存部门用户映射，避免每次重新计算
      cachedDepartmentIdAllUserIdSetMap: null,
      cachedRootDepartmentId: null,
      cachedUsersLength: 0
    }
  },
  computed: {
    rolesMap() {
      return this._.keyBy(this.roles, "id")
    },
    departmentIdAllUserIdSetMap() {
      // 返回缓存的映射，如果缓存不存在则计算
      if (!this.cachedDepartmentIdAllUserIdSetMap) {
        this.updateDepartmentMapCache()
      }
      return this.cachedDepartmentIdAllUserIdSetMap
    },
    tableData() {
      let users = this.curCompanyUsers
      if (this.currentDepartment.id !== this.rootDepartment.id) {
        const departmentUserSet = this.departmentIdAllUserIdSetMap[this.currentDepartment.id]
        if (!departmentUserSet) return []
        users = users.filter(user => departmentUserSet.has(user.id))
      }
      
      // 返回新对象而不是修改原对象，避免触发响应式更新
      return users
        .map(u => {
          const isMaster = this.currentDepartment.adminIds?.includes(u.id)
          const state = u.status === "正常" ? 0 : u.status === "未激活" ? 1 : 2
          let role = ""
          u.companyRoleIds.forEach(
            (roleId, index) =>
              (role = role + (index === 0 ? "" : "，") + (this.rolesMap[roleId]?.name || ""))
          )
          
          // 返回新对象，避免修改原对象
          return {
            ...u,
            isMaster,
            state,
            role
          }
        })
        .sort((uA, uB) => {
          if (uA.state !== uB.state) return uA.state - uB.state
          if (uA.name !== uB.name) return uA.name.localeCompare(uB.name)
          return 0
        })
    },
    filterData() {
      let { searchKey, searchInfo } = this.searchBody
      if (searchInfo === undefined) {
        return this.tableData
      }
      return this.tableData.filter(data => {
        if (["name", "telephone", "status"].includes(searchKey))
          return data[searchKey].includes(searchInfo.searchText)
        if (searchKey === "companyRoleName") {
          return this._.isEmpty(searchInfo.searchArray)
            ? true
            : searchInfo.searchArray.some(id => data.companyRoleIds.includes(id))
        }
        return false
      })
    }
  },
  created() {
    getCompanyRolesWithNum().then(data => (this.roles = data))
  },
  watch: {
    "filterData.length": function() {
      this.pageInfo.total = this.filterData?.length || 0
    },
    // 监听依赖变化，更新缓存
    rootDepartment: {
      handler() {
        this.updateDepartmentMapCache()
      },
      deep: true
    },
    curCompanyUsers: {
      handler() {
        this.updateDepartmentMapCache()
      },
      deep: false // 只监听数组长度变化，不需要深度监听
    }
  },
  methods: {
    updateDepartmentMapCache() {
      const rootId = this.rootDepartment?.id
      const usersLength = this.curCompanyUsers?.length || 0
      
      // 只在依赖真正变化时重新计算
      if (
        !this.cachedDepartmentIdAllUserIdSetMap ||
        this.cachedRootDepartmentId !== rootId ||
        this.cachedUsersLength !== usersLength
      ) {
        this.cachedDepartmentIdAllUserIdSetMap = calcDepartmentIdAllUserIdSetMap(
          this.rootDepartment,
          this.curCompanyUsers
        )
        this.cachedRootDepartmentId = rootId
        this.cachedUsersLength = usersLength
      }
    },
    handleSearch(searchBody) {
      this.searchBody = searchBody
      this.pageInfo.currentPage = 1
    },
    handleSelectDpt(dpt) {
      this.currentDepartment = dpt
      this.pageInfo.currentPage = 1
    }
  }
}
</script>

<style lang="scss" scoped>
.member-table {
  height: 100%;
  .base-table {
    height: 100%;
  }
  .admin-icon {
    color: #a8a8a8;
  }
}
</style>
